// Pure logic for the BTL Scholarship grant/manage surface, kept separate from
// the components so the duration → absolute-instant conversion, the "no
// duration chosen yet" gate, and the no-Stripe-subscription comp predicate
// can be unit tested without rendering anything.

/**
 * DurationChoice models the grant sheet's duration control. There is
 * deliberately no default: the caller must construct one of these three
 * variants explicitly, and the sheet itself starts with `choice: null`
 * (see grant-scholarship-sheet.tsx) — nothing here "falls back" to permanent
 * or to any preset.
 */
export type DurationChoice =
  | { kind: "preset"; months: 1 | 3 | 6 | 12 }
  | { kind: "custom"; date: Date }
  | { kind: "permanent" };

export const DURATION_PRESET_MONTHS = [1, 3, 6, 12] as const;

/**
 * Resolves a duration choice to an absolute instant, or null for a permanent
 * grant. This is the ONLY place clock arithmetic happens — the resolved
 * instant is what gets sent to the server (GrantMembershipRequest.expires_at
 * is an absolute timestamp; the proto comment is explicit that no
 * duration/clock arithmetic happens server-side).
 */
export function resolveDurationToInstant(
  choice: DurationChoice,
  now: Date = new Date(),
): Date | null {
  switch (choice.kind) {
    case "permanent":
      return null;
    case "custom":
      return choice.date;
    case "preset": {
      const resolved = new Date(now);
      resolved.setUTCMonth(resolved.getUTCMonth() + choice.months);
      return resolved;
    }
    default: {
      const exhaustive: never = choice;
      throw new Error(`unhandled duration choice: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/**
 * canSubmitGrant is the single source of truth for whether the grant sheet's
 * submit button is enabled. A grant can never go through with no duration
 * chosen (choice === null), a custom date that hasn't been entered, an
 * unselected tier, or a reason under MIN_REASON_LENGTH — matching the
 * server-side minimum enforced in admin-service (see memberships.go's
 * minReasonLength) so a short reason is caught here, not discovered via a
 * 400 after a round trip.
 */
export const MIN_REASON_LENGTH = 3;

export function canSubmitGrant(params: {
  tierId: string | null;
  reason: string;
  choice: DurationChoice | null;
}): boolean {
  if (!params.tierId) return false;
  if (params.reason.trim().length < MIN_REASON_LENGTH) return false;
  if (params.choice === null) return false;
  if (params.choice.kind === "custom" && Number.isNaN(params.choice.date.getTime())) return false;
  return true;
}

const CONSEQUENCE_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * formatConsequence renders the grant sheet's live "what will actually
 * happen" panel. Kept to exactly two facts: when access ends, and whether
 * the grant can be extended afterward — no explanatory prose.
 *
 * `expiresAt` can be an Invalid Date here: the custom-date chip sets a
 * `{ kind: "custom", date: new Date(NaN) }` choice the instant it's clicked,
 * before the user has picked an actual date (see grant-scholarship-sheet.tsx).
 * `Intl.DateTimeFormat.format()` throws ("date value is not finite") on an
 * Invalid Date, so that case is handled explicitly here rather than left to
 * whatever the caller happens to guard.
 */
export function formatConsequence(params: { expiresAt: Date | null }): string {
  const { expiresAt } = params;
  if (expiresAt === null) {
    return "Never expires.";
  }
  if (Number.isNaN(expiresAt.getTime())) {
    return "Pick a date to see when access ends.";
  }
  const formatted = CONSEQUENCE_DATE_FORMAT.format(expiresAt);
  return `Access ends ${formatted}. Can be extended afterward.`;
}

/**
 * MembershipTierLike is the minimal shape selectCanonicalTierOptions needs —
 * structurally compatible with admin-api's MembershipTierDTO, kept local so
 * this pure-logic module doesn't import the data layer.
 */
export interface MembershipTierLike {
  id: string;
  isActive: boolean;
  currency?: string;
  platformTierMapping?: string;
  billingOptions: { billingCycle: string; isActive: boolean }[];
}

export interface CanonicalTierOption {
  id: string;
  label: string;
}

/**
 * CANONICAL_PLATFORM_TIERS is the fixed, exhaustive set of tiers a
 * scholarship can grant. Deliberately excludes USER_TIER_FREE: a scholarship
 * grants access UP from Free, it never grants Free itself.
 */
const CANONICAL_PLATFORM_TIERS: ReadonlyArray<{ mapping: string; label: string }> = [
  { mapping: "USER_TIER_PRO", label: "Pro" },
  { mapping: "USER_TIER_LINE_BREAKERS", label: "Line Breakers" },
];

/**
 * selectCanonicalTierOptions collapses the full tier list — which carries a
 * separate row per currency x cadence combination purely as a Stripe billing
 * artifact (see multi-cadence-prod-tier-data-shape) — down to exactly one
 * option per platform tier a comp can actually grant: Pro and Line Breakers.
 *
 * A comp's granted amount is always 0, so currency and cadence are cosmetic
 * for this screen: every variant of a given platformTierMapping grants
 * identical access. Offering all nine as if they were meaningfully different
 * choices would be a papercut on a screen whose entire job is an unambiguous
 * choice.
 *
 * Selection is driven ONLY by platformTierMapping (never by name-matching,
 * which breaks the moment a tier is renamed). Within a mapping, preference
 * order is: GBP + an active monthly billing option (matches the one
 * scholarship already live in prod, @cfcparth on the Line Breakers GBP
 * monthly row) > any GBP row > the first active row for that mapping.
 */
export function selectCanonicalTierOptions(tiers: MembershipTierLike[]): CanonicalTierOption[] {
  const options: CanonicalTierOption[] = [];
  for (const { mapping, label } of CANONICAL_PLATFORM_TIERS) {
    const candidates = tiers.filter((t) => t.isActive && t.platformTierMapping === mapping);
    if (candidates.length === 0) continue;

    const gbpMonthly = candidates.find(
      (t) =>
        t.currency === "gbp" &&
        t.billingOptions.some((o) => o.isActive && o.billingCycle === "BILLING_CYCLE_MONTHLY"),
    );
    const gbp = candidates.find((t) => t.currency === "gbp");
    const chosen = gbpMonthly ?? gbp ?? candidates[0];
    options.push({ id: chosen.id, label });
  }
  return options;
}

/**
 * isComplimentaryMembership mirrors the CRITICAL domain rule (verified in
 * prod): a comp is identified by having NO Stripe subscription, NEVER by
 * amountCents being zero. admin-service already computes hasStripeSubscription
 * server-side (toMembershipDTO in memberships.go) precisely so no caller has
 * to re-derive it from amount — this helper trusts that field, not amount, on
 * purpose, and exists so any UI that needs the predicate reads it from one
 * place rather than reimplementing (and risking reintroducing the
 * amount-based bug) inline.
 */
export function isComplimentaryMembership(membership: { hasStripeSubscription: boolean }): boolean {
  return !membership.hasStripeSubscription;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** daysRemaining returns whole days until `expiresAt`, negative if already past. */
export function daysRemaining(expiresAt: Date, now: Date = new Date()): number {
  return Math.ceil((expiresAt.getTime() - now.getTime()) / MS_PER_DAY);
}

/**
 * ScholarshipGroup partitions the manage view exactly on whether expires_at
 * is set — the ONLY signal a membership is time-boxed (see the domain rules:
 * NULL means permanent and invisible to the sweeper; any non-null value means
 * "ends at exactly this instant"). There is no third bucket for "time-boxed
 * but far out": "when does this end" is the one question the manage view
 * answers, so every time-boxed row belongs under "Expiring soon", sorted by
 * how soon, and every open-ended row belongs under "Permanent".
 */
export type ScholarshipGroup = "expiring-soon" | "permanent";

export function groupForMembership(membership: { expiresAt?: string | null }): ScholarshipGroup {
  if (!membership.expiresAt) return "permanent";
  const expiresAt = new Date(membership.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return "permanent";
  return "expiring-soon";
}

const EXPIRY_DATE_FORMAT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * membershipExpiryLabel is the single line of copy every surface needs for
 * "when does this end": "Forever" when expiresAt is null/undefined/unparseable
 * (the same permanent bucket groupForMembership uses — an unparseable value is
 * never treated as a date, only ever as "no expiry set"), or "Until {date}"
 * for a time-boxed membership. Centralized so the user-detail subscription
 * card and the manage list format expiry identically instead of each
 * re-deriving the permanent/invalid-date fallback separately.
 */
export function membershipExpiryLabel(membership: { expiresAt?: string | null }): string {
  if (!membership.expiresAt) return "Forever";
  const expiresAt = new Date(membership.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return "Forever";
  return `Until ${EXPIRY_DATE_FORMAT.format(expiresAt)}`;
}

type SubscriberIdentity = {
  subscriberId: string;
  subscriberUsername?: string;
  subscriberDisplayName?: string;
};

/**
 * membershipSubscriberLabel is the one line of copy the manage list shows for
 * "who is this". admin-service (PR #32) added subscriberUsername/
 * subscriberDisplayName to MembershipDTO, but older deploys/fixtures omit
 * them — this always falls back to the raw subscriberId (a UUID) rather than
 * rendering nothing.
 */
export function membershipSubscriberLabel(membership: SubscriberIdentity): string {
  return (
    membership.subscriberDisplayName || membership.subscriberUsername || membership.subscriberId
  );
}

/**
 * membershipSubscriberHandle returns the secondary "@handle" line to show
 * under the primary label — only when the primary label came from
 * subscriberDisplayName, so the username isn't shown twice when it was
 * already used as the primary label.
 */
export function membershipSubscriberHandle(membership: SubscriberIdentity): string | null {
  if (membership.subscriberDisplayName && membership.subscriberUsername) {
    return `@${membership.subscriberUsername}`;
  }
  return null;
}

type TierNameLookup = { id: string; name: string };

/**
 * buildTierNameMap turns the platform's membership tier list (from
 * listPlatformMembershipTiers) into an id -> display name lookup, so the
 * manage view can resolve a scholarship's tier_id to a human-readable name
 * instead of a raw UUID.
 */
export function buildTierNameMap(tiers: TierNameLookup[]): Map<string, string> {
  return new Map(tiers.map((tier) => [tier.id, tier.name]));
}

/**
 * resolveTierName renders a membership's tier NAME, never its raw id: it
 * prefers the live lookup built by buildTierNameMap (keyed on tierId), then
 * the membership's own tierName field (populated on some legacy shapes),
 * and only falls back to the raw tierId if neither resolves — e.g. the tier
 * list hasn't loaded yet, or the tier has since been deleted.
 */
export function resolveTierName(
  membership: { tierId: string; tierName?: string },
  tierMap: Map<string, string>,
): string {
  return tierMap.get(membership.tierId) || membership.tierName || membership.tierId;
}
